import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { TabNav, type TabNavItem } from '@/components/ui/TabNav'
import { cn } from '@/lib/cn'
import {
  gapText,
  readinessRowLabel,
  type SectionMarker,
} from '@/features/quotes/quoteReadiness'
import {
  sectionDomId,
  sectionHeadingId,
  type QuoteSectionKey,
} from '@/features/quotes/sectionSpy'

/* The quote as ONE sheet (S-3): five sections stacked in one scrolling column,
 * and a sticky bar of links to them. This replaced seven tabs (UX-DR-9 said
 * "sections are tabs, not a stack"; decision s3-quote-one-sheet supersedes it):
 * a reader checking the costing against the cargo had to switch between them,
 * and the readiness card could not say WHERE a gap was fixed.
 *
 * The bar is the old tab rail's TabNav, now with `current="location"`: the
 * active link is where the reader is scrolled to, decided by `useSectionSpy`,
 * and a press scrolls rather than swaps. It writes no URL.
 */

/** The bar's height in pixels. A press lands a section exactly under the
 *  RENDERED bar (`landingScrollTop` in `useSectionSpy`, which ignores
 *  `scroll-margin`); this is what it renders at, and the sections' scroll
 *  margin repeats it only for the browser's own scrolls (find in page, focus
 *  from the keyboard). */
export const SECTION_BAR_HEIGHT = 48

/** AppShell's `<main>` top padding (`pt-6`, the same at every width).
 *
 *  Chrome sticks an element inside the scrollport DEFLATED by the scroll
 *  container's padding, so `top: 0` held the bar 24px below `<main>`'s top
 *  edge: a strip of content showed through above it, and a section landing at
 *  the bar's height went under it (browser check, S-3). A negative `top` of
 *  exactly that padding sticks the bar flush to the edge. Change both together
 *  if the shell's padding changes. */
const SHELL_SCROLL_PADDING_TOP = 24

export function QuoteSectionBar({
  sections,
  active,
  onGo,
  markers,
  attach,
  action,
}: {
  sections: readonly QuoteSectionKey[]
  active: QuoteSectionKey
  onGo: (key: QuoteSectionKey) => void
  /** Readiness markers, on a draft only. Absent means no markers at all. */
  markers?: Partial<Record<QuoteSectionKey, SectionMarker>>
  /** The spy's anchor: the bar sits inside the page's scroll container. */
  attach?: (element: HTMLElement | null) => void
  /** The primary action — Send on a draft the reader may send. */
  action?: ReactNode
}) {
  const { t } = useTranslation()

  const items: TabNavItem<QuoteSectionKey>[] = sections.map((key) => {
    const marker = markers?.[key]
    return {
      key,
      labelKey: `quotes.sections.${key}`,
      ...(marker
        ? {
            badge: marker.badge,
            badgeTone: marker.tone,
            badgeLabel: marker.rows
              .map((row) =>
                t('quotes.readiness.marker', {
                  row: readinessRowLabel(row.key, t),
                  gap: row.gap ? gapText(row.gap, t) : '',
                }),
              )
              .join('; '),
          }
        : {}),
    }
  })

  return (
    /* Sticky inside AppShell's `<main>`, which is the scroll container, flush
       to its top edge (see SHELL_SCROLL_PADDING_TOP), at a FIXED height, so a
       press can land a section exactly under it. The page
       colour rather than a card surface: the bar is the page's furniture, and
       content scrolls under it rather than behind a floating panel — no
       shadow, the hairline under it is the edge. On a phone the links scroll
       sideways inside TabNav and the action keeps its place. */
    <div
      ref={attach}
      style={{ height: SECTION_BAR_HEIGHT, top: -SHELL_SCROLL_PADDING_TOP }}
      className="bg-surface-page border-line sticky z-20 flex min-w-0 items-end gap-3 border-b"
    >
      <TabNav
        label={t('quotes.sectionsLabel')}
        items={items}
        value={active}
        onChange={onGo}
        current="location"
        className="min-w-0 flex-1 border-b-0"
      />
      {action ? <div className="flex shrink-0 items-center self-center">{action}</div> : null}
    </div>
  )
}

/** One section of the sheet: an anchor, a heading, and whatever the section
 *  holds. Plain markup and NOT a Card — what goes inside is already cards
 *  (the terms are three), and a card in a card is a frame around a frame.
 *  Never collapses: the point of the sheet is that it is all there. */
export function QuoteSection({
  sectionKey,
  children,
  className,
}: {
  sectionKey: QuoteSectionKey
  children: ReactNode
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <section
      id={sectionDomId(sectionKey)}
      aria-labelledby={sectionHeadingId(sectionKey)}
      style={{ scrollMarginTop: SECTION_BAR_HEIGHT }}
      className={cn('flex min-w-0 flex-col gap-3.5', className)}
    >
      {/* Focusable by script only (tabIndex -1): a link to the section moves
          focus here so the keyboard lands where the eye does, without adding
          five tab stops to the page. Regular weight, as every heading is. */}
      <h2
        id={sectionHeadingId(sectionKey)}
        tabIndex={-1}
        className="text-text-heading pt-2 text-[17px] leading-tight font-normal tracking-[-0.3px] focus:outline-none"
      >
        {t(`quotes.sections.${sectionKey}`)}
      </h2>
      {children}
    </section>
  )
}
